import { Button, Form } from '@heroui/react'
import { useCreateQSEEnterpriseTenant, usePlans, useUpdateTenant } from '@qse/api'
import { type ModalComponentProps, QSE_ENTERPRISE_ID, UserRoleEnum } from '@qse/constants'
import { useUserStore } from '@qse/stores'
import { Alert, HeroInput } from '@qse/ui'
import { useState } from 'react'

export default function AddTenant(props: ModalComponentProps) {
  const { setModelActive, onClose, data: configData } = props
  const data = configData?.data
  const isEditMode = !!data?.tenant

  const { mutateAsync: createQSEEnterpriseTenant, isPending: isCreating } =
    useCreateQSEEnterpriseTenant()
  const { mutateAsync: updateTenant, isPending: isUpdating } = useUpdateTenant()
  const { data: plansData, isPending: isLoadingPlans } = usePlans()
  const { user } = useUserStore()

  const [error, setError] = useState('')

  // Build plan options: active plans, plus current plan in edit mode (even if inactive)
  const allPlans = plansData?.data || []
  const currentPlanId = isEditMode ? data?.tenant?.planId : undefined
  const availablePlans = allPlans.filter(plan => plan.isActive || plan.id === currentPlanId)
  const sortedPlans = availablePlans.sort(
    (a, b) => (a.storageQuotaGb || 0) - (b.storageQuotaGb || 0),
  )

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')

    const formDataObj = new FormData(e.currentTarget as HTMLFormElement)

    try {
      const companyName = formDataObj.get('companyName') as string
      const firstName = formDataObj.get('firstName') as string
      const lastName = formDataObj.get('lastName') as string
      const email = formDataObj.get('email') as string
      const phoneNumber = formDataObj.get('phoneNumber') as string
      const planId = formDataObj.get('planId') as string
      const isResellerAdmin = user?.userRole === UserRoleEnum.RESELLER_ADMIN
      const resellerId = isResellerAdmin ? user?.resellerId : QSE_ENTERPRISE_ID
      if (isEditMode) {
        // For edit mode, we update both company name and plan
        if (!companyName?.trim()) {
          setError('Company name is required')

          return
        }

        if (!planId?.trim()) {
          setError('Please select a plan')

          return
        }

        await updateTenant(
          {
            id: data.tenant.id,
            tenantData: {
              name: companyName.trim(),
              planId: planId.trim(),
            },
          },
          {
            onSuccess: () => {
              setModelActive(undefined)
              onClose()
            },
            onError: error => {
              setError(
                error?.response?.data?.error?.details?.message ||
                  error.message ||
                  'Failed to update tenant',
              )
            },
          },
        )

        return
      }

      // Create new tenant
      const payload = {
        companyName: companyName.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        phoneNumber: phoneNumber.trim(),
        email: email.trim(),
        resellerId,
        planId: planId.trim(),
      }

      await createQSEEnterpriseTenant(payload as any, {
        onSuccess: () => {
          setModelActive(undefined)
          onClose()
        },
        onError: error => {
          setError(
            error?.response?.data?.error?.details?.message ||
              error.message ||
              'Failed to create tenant',
          )
        },
      })
    } catch (error: unknown) {
      setError(error instanceof Error ? error.message : 'An unexpected error occurred')
    }
  }

  const handleCancel = () => {
    setModelActive(undefined)
    onClose()
  }

  return (
    <div>
      <Form
        className="flex flex-col gap-5 py-5 w-full"
        validationBehavior="native"
        onSubmit={handleSubmit}
      >
        {error && <Alert color="danger" description={error} />}

        {!isEditMode && (
          <HeroInput
            isReadOnly
            isRequired
            control="text"
            label="Tenant Type"
            labelPlacement="outside"
            name="resellerId"
            value={'QSE Enterprise'}
            variant="bordered"
          />
        )}
        {/* Company Name */}
        <HeroInput
          control="text"
          defaultValue={isEditMode ? data?.tenant?.name || '' : ''}
          label="Company Name"
          labelPlacement="outside"
          name="companyName"
          placeholder="Enter company name"
          validate={(value: string) => {
            if (!value?.trim()) {
              return 'Company name is required'
            }
            if (value.trim().length < 2) {
              return 'Company name must be at least 2 characters'
            }
            if (value.trim().length > 100) {
              return 'Company name must be less than 100 characters'
            }
          }}
        />

        {/* Primary Contact Details - Only show in create mode */}
        {!isEditMode && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 w-full">
              <HeroInput
                isRequired
                control="text"
                defaultValue=""
                label="First Name"
                labelPlacement="outside"
                name="firstName"
                placeholder="Enter first name"
                validate={(value: string) => {
                  if (!value?.trim()) {
                    return 'First name is required'
                  }
                  if (value.trim().length > 50) {
                    return 'First name must be less than 50 characters'
                  }
                }}
              />

              <HeroInput
                isRequired
                control="text"
                defaultValue=""
                label="Last Name"
                labelPlacement="outside"
                name="lastName"
                placeholder="Enter last name"
                validate={(value: string) => {
                  if (!value?.trim()) {
                    return 'Last name is required'
                  }
                  if (value.trim().length > 50) {
                    return 'Last name must be less than 50 characters'
                  }
                }}
              />
            </div>

            <HeroInput
              isRequired
              control="email"
              defaultValue=""
              label="Email Address"
              labelPlacement="outside"
              name="email"
              placeholder="Enter admin email address"
              type="email"
              variant="bordered"
              validate={(value: string) => {
                if (!value?.trim()) {
                  return 'Email address is required'
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

                if (!emailRegex.test(value)) {
                  return 'Please enter a valid email address'
                }
              }}
            />

            <HeroInput
              isRequired
              control="phone"
              defaultValue=""
              label="Phone Number"
              labelPlacement="outside"
              name="phoneNumber"
              placeholder="Enter phone number"
              validate={(value: string) => {
                if (!value?.trim()) {
                  return 'Phone number is required'
                }
                const phoneRegex = /^[+]?[1-9][\d]{0,15}$/

                if (!phoneRegex.test(value.replace(/[\s\-()]/g, ''))) {
                  return 'Please enter a valid phone number'
                }
              }}
            />
          </>
        )}

        {/* Plan Selection */}
        <HeroInput
          isRequired
          control="select"
          defaultValue={isEditMode ? data?.tenant?.planId || '' : ''}
          isLoading={isLoadingPlans}
          label={isEditMode ? 'Current Plan' : 'Plan Selection'}
          labelPlacement="outside"
          name="planId"
          placeholder="Select a plan"
          variant="bordered"
          options={sortedPlans.map(plan => ({
            key: plan.id,
            label: `${plan.name} - ${plan.storageQuotaGb || 0} GB Storage - ${plan.serviceUsageQuota || 0} API Calls - ${plan.qpaSeedCount || 0} QPA Seeds`,
          }))}
          validate={(value: string) => {
            if (!value?.trim()) {
              return 'Please select a plan'
            }
          }}
        />

        <div className="flex justify-end gap-3 w-full">
          <Button
            disabled={isCreating || isUpdating}
            radius="sm"
            variant="flat"
            onPress={handleCancel}
          >
            Cancel
          </Button>
          <Button
            color="primary"
            isDisabled={isLoadingPlans}
            isLoading={isCreating || isUpdating}
            radius="sm"
            type="submit"
          >
            {isEditMode ? 'Update Tenant' : 'Create Tenant'}
          </Button>
        </div>
      </Form>
    </div>
  )
}
